import { useEffect, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import {
  ALL_SEARCH_KINDS,
  SEARCH_KIND_LABEL,
  type SearchResult,
} from '../../api/searchTypes';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import styles from './GlobalSearchInput.module.css';

// WS-4a (C2) — header-mounted global search. Inline dropdown of the
// top results across DM/ICN/PM/User. "View all results" routes to
// `/search?q=...`. Cmd/Ctrl+K is part of C6 (global keyboard hook)
// and intentionally NOT wired here per the WS-4a split.
//
// Per spec/00 §0.11 rule 20: every dropdown row is a real navigation
// to an existing route. DM hits go to `/projects/:projectId/csdb/editor/:dmId`
// — the existing embedded editor mount — never a parallel path.

const DEBOUNCE_MS = 200;

export function GlobalSearchInput(): JSX.Element {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, open, () => setOpen(false));
  useEscapeKey(open, () => setOpen(false));

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  // 03-Jun QA report #17 (Batch 2 2026-07-02) — pre-fix the header
  // search always hit `/api/v1/search` unscoped, so a user inside
  // `Bike Sample` who typed "Manual Test" saw hits from every
  // project they could read. The tester then opened one of those
  // cross-project hits and hit a stale-back-nav 404.
  //
  // The backend already accepts an optional `project=<uuid>` query
  // param (see `src/services/searchService.ts:91-102`) and the
  // `useGlobalSearch` hook already forwards `projectId` when
  // provided. The missing piece was here: derive the current
  // project id from the route via `useMatch('/projects/:id/*')` and
  // pass it in.
  //
  // Result-link construction is unchanged — the backend still returns
  // `/projects/${hit.projectId}/…` using the target DM's own project,
  // so opening a hit still navigates to the correct project when the
  // scope is intentionally cleared (e.g. from a route outside any
  // project).
  const projectMatch = useMatch('/projects/:id/*');
  const projectId = projectMatch?.params.id;

  const query = useGlobalSearch({
    q: debounced,
    types: ALL_SEARCH_KINDS.slice(),
    projectId,
    page: 1,
    limit: 8,
    enabled: open && debounced.length > 0,
  });

  function go(hit: SearchResult): void {
    setOpen(false);
    setQ('');
    setDebounced('');
    navigate(hit.link);
  }

  function viewAll(): void {
    if (debounced.length === 0) return;
    setOpen(false);
    // 03-Jun QA report #17 — preserve the project scope on the
    // "View all results" navigation too so the dedicated search page
    // stays in-project when the header was scoped.
    const projectParam = projectId ? `&project=${encodeURIComponent(projectId)}` : '';
    navigate(`/search?q=${encodeURIComponent(debounced)}${projectParam}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && debounced.length > 0) {
      e.preventDefault();
      viewAll();
    }
  }

  const items = query.data?.results ?? [];

  return (
    <div ref={rootRef} className={styles.root} data-testid="global-search">
      <div className={styles.field}>
        <Search size={14} strokeWidth={1.5} aria-hidden="true" className={styles.icon} />
        <input
          type="search"
          className={styles.input}
          placeholder={
            projectId
              ? 'Search this project…'
              : 'Search DMs, graphics, PMs, users…'
          }
          aria-label="Global search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          data-testid="global-search-input"
        />
      </div>
      {open && debounced.length > 0 ? (
        <div
          className={styles.dropdown}
          role="listbox"
          data-testid="global-search-dropdown"
        >
          {query.isLoading ? (
            <div className={styles.empty}>Searching…</div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>No matches for “{debounced}”.</div>
          ) : (
            <ul className={styles.list}>
              {items.map((hit) => (
                <li key={`${hit.type}-${hit.id}`}>
                  <button
                    type="button"
                    className={styles.row}
                    role="option"
                    aria-selected={false}
                    onClick={() => go(hit)}
                    data-testid={`global-search-hit-${hit.type}-${hit.id}`}
                  >
                    <span className={styles.rowKind}>
                      {SEARCH_KIND_LABEL[hit.type]}
                    </span>
                    <span className={styles.rowTitle}>{hit.primary}</span>
                    {hit.secondary ? (
                      <span className={styles.rowSubtitle}>{hit.secondary}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className={styles.viewAll}
            onClick={viewAll}
            data-testid="global-search-view-all"
            disabled={debounced.length === 0}
          >
            View all results for “{debounced}”
          </button>
        </div>
      ) : null}
    </div>
  );
}
